import { brandLinka } from '@ads/tokens';
import type { ProductSoldByTypeData } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { formatNumber } from '@linka/utils';
import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../../../chart/chart';
import { ChartCard, ChartCardContent, ChartCardHeader, ChartCardTitle, ExportChartTriggerButton } from '../components';

interface ProductsByTypeProps {
  data: ProductSoldByTypeData[];
}

export const ProductsByType = ({ data }: ProductsByTypeProps) => {
  const t = useTranslation();
  const [legendTarget, setLegendTarget] = useState<HTMLDivElement | null>(null);
  const chartConfig = {
    confirmed: { label: t('dashboard.products.confirmed'), color: brandLinka.baseColor.brand['400'] },
    processing: { label: t('dashboard.products.processing'), color: brandLinka.baseColor.warning['400'] },
    cancelledOrRefunded: {
      label: t('dashboard.products.cancelled-refunded'),
      color: brandLinka.baseColor.danger['600'],
    },
  };

  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.products-sold-by-type')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_PRODUCTS_SOLD_BY_TYPE' />
      </ChartCardHeader>
      {data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent className='flex flex-col'>
          <div className='flex'>
            <ChartContainer config={chartConfig} className='w-[60px]'>
              <BarChart data={data} responsive accessibilityLayer margin={{ top: 24 }} width={60}>
                <CartesianGrid vertical={false} />
                <Bar dataKey='confirmed' fill='transparent' barSize={0} />
                <YAxis
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  width={60}
                  tickFormatter={(value) => formatNumber(value, { notation: 'compact' })}
                />
                <XAxis dataKey='productName' height={120} />
              </BarChart>
            </ChartContainer>

            <div className='flex-1 overflow-x-auto pb-xs'>
              <ChartContainer config={chartConfig} className='w-full' dataLength={data.length}>
                <BarChart data={data} responsive accessibilityLayer margin={{ top: 24 }}>
                  <CartesianGrid vertical={false} />
                  <Bar dataKey='confirmed' stackId='a' fill={chartConfig.confirmed.color} radius={0} />
                  <Bar dataKey='processing' stackId='a' fill={chartConfig.processing.color} radius={0} />
                  <Bar
                    dataKey='cancelledOrRefunded'
                    stackId='a'
                    fill={chartConfig.cancelledOrRefunded.color}
                    radius={[8, 8, 0, 0]}
                  />
                  <XAxis
                    dataKey='productName'
                    tickLine={false}
                    tickMargin={24}
                    tick={{ textAnchor: 'start' }}
                    angle={45}
                    height={120}
                    tickFormatter={(label) => {
                      const maxLength = 12;
                      return String(label).length > maxLength ? `${String(label).substring(0, maxLength)}...` : label;
                    }}
                  />
                  <ChartTooltip
                    cursor={{ fill: brandLinka.contextualColor.interactive.secondary }}
                    content={<ChartTooltipContent />}
                  />
                  {/* `portal` renderiza a legenda fora da área rolável sem reduzir a altura do gráfico (o eixo Y
                      continua alinhado). Fora do ChartContainer as variáveis `--color-*` não existem, por isso o
                      `fill` das barras usa a cor direta: é ela que a legenda lê para pintar o quadradinho. `itemSorter={null}` mantém a ordem das séries, não a alfabética. */}
                  <ChartLegend
                    portal={legendTarget}
                    itemSorter={null}
                    content={<ChartLegendContent className='flex-row flex-wrap justify-center gap-x-xs' />}
                  />
                </BarChart>
              </ChartContainer>
            </div>
          </div>
          <div
            ref={setLegendTarget}
            className='[&_[data-testid]]:!ts-paragraph-xxs [&_.recharts-legend-wrapper]:w-full [&_.recharts-legend-wrapper_div]:whitespace-nowrap'
          />
        </ChartCardContent>
      )}
    </ChartCard>
  );
};
